import * as React from "react"
import { gql } from "@apollo/client"
import { Box, Button, Link, Stack, Tooltip, VStack } from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import {
  EventDetailFragment,
  GetProfileStatsDocument,
  useAttendEventMutation,
  useUnAttendEventMutation,
} from "lib/graphql"
import { useMe } from "lib/hooks/useMe"
import { useMutationHandler } from "lib/hooks/useMutationHandler"

import { ShortRegisterForm } from "./ShortRegisterForm"
import { LinkButton } from "./LinkButton"

dayjs.extend(utc)

const _ = gql`
  mutation AttendEvent($eventId: String!) {
    attendEvent(eventId: $eventId)
  }

  mutation UnAttendEvent($eventId: String!) {
    unAttendEvent(eventId: $eventId)
  }
`

interface Props {
  event: EventDetailFragment
  refetch: () => void
}

export function EventActions({ event, refetch }: Props) {
  const { me } = useMe()
  const { ticketLink } = event
  const hasExpired = event?.date ? dayjs.utc(event?.date).add(1, "day").isBefore(dayjs().endOf("day")) : false
  const handler = useMutationHandler()

  const isNotAllowedRegion = event?.isRestrictedToRegion && me?.region !== event.region
  const isFull = event?.maxAttendees ? event?.attendeeCount >= event?.maxAttendees : false

  const [attendEvent] = useAttendEventMutation({
    refetchQueries: [{ query: GetProfileStatsDocument, variables: { userId: me?.id } }],
  })
  const [unAttendEvent] = useUnAttendEventMutation({
    refetchQueries: [{ query: GetProfileStatsDocument, variables: { userId: me?.id } }],
  })

  const handleAttend = () => {
    if (!me) return
    if (event.isAttending) {
      return handler(() => unAttendEvent({ variables: { eventId: event.id } }), {
        onSuccess: (_, toast) => {
          refetch()
          toast({ description: "Afmelden agenda" })
        },
      })
    } else {
      return handler(() => attendEvent({ variables: { eventId: event.id } }), {
        onSuccess: (_, toast) => {
          refetch()
          toast({ description: "Aangemeld voor agenda" })
        },
      })
    }
  }

  if (!event?.isAttendable) return null

  return (
    <VStack w="100%" justify="space-between">
      {me ? (
        <VStack w="100%">
          <Tooltip
            label={
              hasExpired
                ? "Event verstreken"
                : isNotAllowedRegion && `Deze event is alleen toegankelijk voor members uit ${event.region}`
            }
            hasArrow
            zIndex={10}
          > 
            <Box w="100%">
              <Button
                w="100%"
                onClick={handleAttend}
                variant="pink"
                isDisabled={hasExpired || isNotAllowedRegion || (isFull && !event.isAttending)}
              >
                {event.isAttending ? "Afmelden" : "Aanmelden"}
              </Button>
            </Box>
          </Tooltip>
          {ticketLink && !hasExpired && (
            <Link w="100%" href={ticketLink} arial-label="Buy Tickets" isExternal>
              <Button variant="solid" colorScheme="gray" w="100%">
                Tickets
              </Button>
            </Link>
          )}
        </VStack>
      ) : (
        <Stack w="100%">
          <ShortRegisterForm redirect={`/te-doen/${event.slug}`} />
          <LinkButton variant="link" href={`/login?redirect=/te-doen/${event.slug}`}>
            heb je al een account? login
          </LinkButton>
        </Stack>
      )}
    </VStack>
  )
}
